/**
 *  @description Base class for the scenes in the application.
 *  A scene is a container of the different data and settings in the viewer.
 *
 * It needs to contain the following:
 *  init()
 *  animate()
 *  render()
 *  close()
 */

import * as THREE from 'three';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer';

export class Scene extends THREE.Scene {
  protected initialized: boolean;

  constructor() {
    super();

    this.initialized = false;
  }

  isInitalized() {
    return this.initialized;
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async init(defaultTimelineContainerId?: string) {
    throw new Error('init needs to be implemented');
  }

  animate() {
    throw new Error('animate needs to be implemented');
  }

  render(renderer: THREE.Renderer, css2dRenderer?: CSS2DRenderer) {
    throw new Error('render needs to be implemented');
  }

  clearData() {
    throw new Error('clearData needs to be implemented');
  }

  clearCustom() {
    throw new Error('clearCustom needs to be implemented');
  }

  isLoading() {
    throw new Error('isLoading needs to be implemented');
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async activate() {
    throw new Error('activate needs to be implemented');
  }

  deactivate() {
    throw new Error('deactivate needs to be implemented');
  }

  valid() {
    throw new Error('valid needs to be implemented');
  }

  getGeodataBounderies(): THREE.Box3 {
    throw new Error('getGeodataBounderies needs to be implemented');
  }

  /**
   *  Clears all of the children for a THREE.Mesh
   */
  clearChildren(child: THREE.Mesh) {
    if (child.geometry) {
      child.geometry.dispose();
    }
    if (child.material) {
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.map((material) => material.dispose());
    }

    for (let i = child.children.length - 1; i >= 0; i--) {
      this.clearChildren(child.children[i] as THREE.Mesh);
    }

    if (child.parent !== undefined && child.parent !== null) {
      child.parent.remove(child);
    }
  }
}
